'use client';

import { FC, ReactNode, useMemo, useState } from 'react';
import { HeaderSection } from '../../shared/HeaderSection';
import {
  SegmentedControl,
  SegmentedControlItem,
  Skeleton,
} from '../../../core-components';
import { DashboardCardGrid, DashboardCardGridItem } from '../DashboardCardGrid';

export type RelevantInterestingSectionCardItem = DashboardCardGridItem;

export type RelevantInterestingSectionCategory = {
  header?: {
    headline?: string;
    subline?: ReactNode;
  };
  items: RelevantInterestingSectionCardItem[];
  segmentedControlProps?: {
    label?: string;
    'data-track-click-element'?: string;
    'data-track-click-label'?: string;
  };
};

export type RelevantInterestingSectionProps = {
  fallback?: boolean;
  categories?: RelevantInterestingSectionCategory[];
  className?: string;
};

export const RelevantInterestingSection: FC<
  RelevantInterestingSectionProps
> = ({ fallback, categories = [], className }) => {
  if (fallback) {
    return (
      <div className={className}>
        <RelevantInterestingSectionMobile fallback categories={categories} />
        <RelevantInterestingSectionDesktop fallback categories={categories} />
      </div>
    );
  }

  return (
    <div className={className}>
      <RelevantInterestingSectionMobile categories={categories} />
      <RelevantInterestingSectionDesktop categories={categories} />
    </div>
  );
};

type MobileProps = {
  fallback?: boolean;
  categories: RelevantInterestingSectionCategory[];
};

const RelevantInterestingSectionMobile: FC<MobileProps> = ({
  fallback,
  categories,
}) => {
  const [selectedSegment, setSelectedSegment] = useState<string>('0');

  const currentItems = useMemo(() => {
    const categoryIndex = parseInt(selectedSegment, 10);
    return categories[categoryIndex]?.items || [];
  }, [selectedSegment, categories]);

  if (fallback) {
    return (
      <div className="lg:hidden">
        <div className="flex flex-col gap-4 text-marine-high-emphasis pt-10 pb-12">
          <Skeleton className="h-10 w-full rounded-sm" />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {Array.from({ length: 6 }, (_, idx) => (
              <Skeleton key={idx} className="aspect-square w-full rounded-sm" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const hasMultipleCategories = categories.length > 1;

  return (
    <div className="lg:hidden">
      <div className="flex flex-col gap-4 text-marine-high-emphasis pt-10 pb-12">
        {hasMultipleCategories && (
          <SegmentedControl
            value={selectedSegment}
            onValueChange={setSelectedSegment}
          >
            {categories.map((category, idx) => (
              <SegmentedControlItem
                key={idx}
                value={String(idx)}
                label={
                  category.segmentedControlProps?.label ||
                  category.header?.headline ||
                  ''
                }
                {...(category.segmentedControlProps || {})}
              />
            ))}
          </SegmentedControl>
        )}
        <DashboardCardGrid items={currentItems} />
      </div>
    </div>
  );
};

type DesktopProps = {
  fallback?: boolean;
  categories: RelevantInterestingSectionCategory[];
};

const RelevantInterestingSectionDesktop: FC<DesktopProps> = ({
  fallback,
  categories,
}) => {
  if (fallback) {
    return (
      <div className="hidden lg:block space-y-10">
        {Array.from({ length: 2 }, (_, categoryIdx) => (
          <div
            key={categoryIdx}
            className="flex flex-col gap-6 bg-section-gray text-marine-high-emphasis p-6"
          >
            <Skeleton className="h-6 w-1/3" />
            <div className="grid grid-cols-4 gap-4 xl:grid-cols-5">
              {Array.from({ length: 8 }, (_, idx) => (
                <Skeleton
                  key={idx}
                  className="aspect-square w-full rounded-sm"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="hidden lg:block space-y-10">
      {categories.map(
        (category, idx) =>
          category.items.length > 0 && (
            <div
              key={idx}
              className="flex flex-col gap-6 bg-section-gray text-marine-high-emphasis p-6"
            >
              {category.header && (
                <HeaderSection
                  headline={category.header.headline}
                  subline={category.header.subline}
                />
              )}

              <DashboardCardGrid items={category.items} />
            </div>
          ),
      )}
    </div>
  );
};
